import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';

import { MatterType } from '../../services/types/client-intake-types';
import {
  DemandExampleFilters,
  DemandExampleListResponse,
} from '../../services/types/demand-example-types';
import { Input } from '../ui/catalyst/input';
import { Select } from '../ui/catalyst/select';

interface DemandExamplesFiltersProps {
  filters: Partial<DemandExampleFilters>;
  data: DemandExampleListResponse | undefined;
  isLoading: boolean;
  onSearch: (search: string) => void;
  onFilterChange: (key: keyof DemandExampleFilters, value: any) => void;
}

export const DemandExamplesFilters = ({
  filters,
  data,
  isLoading,
  onSearch,
  onFilterChange,
}: DemandExamplesFiltersProps) => {
  // Extract unique values from the data for filter options
  const filterOptions = useMemo(() => {
    if (!data?.results) {
      return { tags: [], jurisdictions: [] };
    }

    const allTags = new Set<string>();
    const allJurisdictions = new Set<string>();

    data.results.forEach((example) => {
      // Collect tags
      example.tags?.forEach((tag) => allTags.add(tag));

      // Collect jurisdictions
      example.jurisdiction?.forEach((jurisdiction) =>
        allJurisdictions.add(jurisdiction),
      );
    });

    return {
      tags: Array.from(allTags).sort(),
      jurisdictions: Array.from(allJurisdictions).sort(),
    };
  }, [data]);

  return (
    <div className="bg-white p-6 rounded-lg border border-zinc-200">
      <div className="flex items-center gap-6">
        {/* Search Bar - Takes up most of the space */}
        <div className="flex-1 relative">
          <div className="relative">
            <Input
              placeholder="Search examples by title, description, tags, jurisdiction, firm..."
              value={filters.search || ''}
              onChange={(e) => onSearch(e.target.value)}
              className="w-full text-base pl-10 [&>input]:!border-0 [&>input]:!ring-0"
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
              {isLoading ? (
                <div className="animate-spin h-4 w-4 border-2 border-zinc-300 border-t-zinc-600 rounded-full" />
              ) : (
                <MagnifyingGlassIcon className="h-4 w-4 text-zinc-400" />
              )}
            </div>
          </div>
        </div>

        {/* Filters - Compact layout on the right */}
        <div className="flex items-center gap-3">
          <div className="text-sm font-medium text-zinc-700 whitespace-nowrap">
            Filter by:
          </div>

          <Select
            value={filters.matter_type || ''}
            onChange={(e) => onFilterChange('matter_type', e.target.value)}
            className="min-w-[140px]"
          >
            <option value="">All Matter Types</option>
            {Object.values(MatterType).map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </Select>

          <Select
            value={filters.jurisdiction || ''}
            onChange={(e) => onFilterChange('jurisdiction', e.target.value)}
            className="min-w-[130px]"
            disabled={!data}
          >
            <option value="">All Jurisdictions</option>
            {filterOptions.jurisdictions.map((jurisdiction) => (
              <option key={jurisdiction} value={jurisdiction}>
                {jurisdiction}
              </option>
            ))}
          </Select>

          <Select
            value={filters.tags || ''}
            onChange={(e) => onFilterChange('tags', e.target.value)}
            className="min-w-[100px]"
            disabled={!data}
          >
            <option value="">All Tags</option>
            {filterOptions.tags.map((tag) => (
              <option key={tag} value={tag}>
                {tag}
              </option>
            ))}
          </Select>

          <Select
            value={
              filters.has_templates === undefined
                ? ''
                : String(filters.has_templates)
            }
            onChange={(e) =>
              onFilterChange(
                'has_templates',
                e.target.value === '' ? undefined : e.target.value === 'true',
              )
            }
            className="min-w-[140px]"
          >
            <option value="">All Examples</option>
            <option value="true">Has Templates</option>
            <option value="false">No Templates</option>
          </Select>

          <Select
            value={filters.ordering || '-created_at'}
            onChange={(e) => onFilterChange('ordering', e.target.value)}
            className="min-w-[130px]"
          >
            <option value="-created_at">Newest First</option>
            <option value="created_at">Oldest First</option>
            <option value="title">Title A-Z</option>
            <option value="-title">Title Z-A</option>
          </Select>
        </div>
      </div>
    </div>
  );
};
